import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Alert, Button, Card, Empty, List, Space, Spin, Tag, Typography } from "antd";
import { ApiService } from "../../api";
import { useTabTitle } from "../../tabs/tabContext";

import { PageHeader } from "../../components/PageHeader";

const { Text } = Typography;

type Row = { id: number; name: string; short_name: string; type: string };

/**
 * Which templates the current user may design the process of.
 *
 * Scoped by the same `type=admin` call the navigation menu uses, so an
 * administrator sees exactly the templates they administer — the designer
 * itself re-checks on the server, this only saves them a dead end.
 */
const ProcessTemplatesPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);

  useTabTitle(t("Process"));

  useEffect(() => {
    (async () => {
      try {
        const types = await ApiService.apiMainTemplateTypesList("admin");
        const flat: Row[] = [];
        types.forEach((type: any) =>
          (type.filtered_templates ?? []).forEach((tpl: any) =>
            flat.push({
              id: tpl.id,
              name: tpl.name,
              short_name: tpl.short_name,
              type: type.name,
            })
          )
        );
        setRows(flat);
      } catch {
        setRows([]);
      }
      setLoading(false);
    })();
  }, []);

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 48 }}>
        <Spin />
      </div>
    );
  }

  return (
    <div>
      <PageHeader title={t("Process")} />
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={t(
          "A template's process is the order its sections are filled and its approvals are collected. Changing it affects permits created from then on."
        )}
      />
      <Card size="small">
        {rows.length === 0 ? (
          <Empty description={t("No templates you can administer")} />
        ) : (
          <List
            dataSource={rows}
            renderItem={(row) => (
              <List.Item>
                <List.Item.Meta
                  title={
                    <Space size={8}>
                      {/* A button, not an anchor: it opens a workspace tab
                          rather than navigating, so it must be reachable by
                          keyboard the way a control is. */}
                      <Button
                        type="link"
                        style={{ padding: 0, height: "auto" }}
                        onClick={() => navigate(`/templates/${row.id}/process`)}
                      >
                        {t(row.name)}
                      </Button>
                      <Tag>{row.short_name}</Tag>
                    </Space>
                  }
                  description={<Text type="secondary">{t(row.type)}</Text>}
                />
              </List.Item>
            )}
          />
        )}
      </Card>
    </div>
  );
};

export default ProcessTemplatesPage;
